import React, {useState, useEffect, useRef} from "react";
import {CSSTransition, TransitionGroup} from "react-transition-group";
import "../../styles/pricing-skpabloocss/main.css"; // Keep mobile styles
import "../../styles/pricing-skpabloocss/icons.css"; // Keep mobile styles
import "../../styles/pabloo-mobile-pricing-styling/mobile-styling.css"; // Keep mobile styles
import {addCartItem} from "../../modules/checkoutHelpers";
import PricingToggle from "./pricingToggle"; // *** IMPORT addCartItem ***

const MobilePricingComponent = ({frequency, handleFrequencyChange}) => {
    const [activeTab, setActiveTab] = useState(frequency || "annually"); // Use passed frequency
    const annuallyRef = useRef(null);
    const monthlyRef = useRef(null);

    // Update activeTab if frequency prop changes
    useEffect(() => {
        setActiveTab(frequency);
    }, [frequency]);

    // No script loading logic needed based on desktop component, keep original cleanup
    useEffect(() => {
        const scripts = [];

        return () => {
            scripts.forEach(({id}) => {
                const script = document.getElementById(id);
                if (script) {
                    // Assuming head, adjust if needed
                    document.head.removeChild(script);
                }
            });
        };
    }, []);

    // Updated feature list based on DesktopPricingComponent rowView calls
    let pricingFeatures = [
        // --- Professional Tier Features (mapped from Desktop's Professional) ---
        {id: 1, title: "Cashback technology", description: "", tier: "professional", isHeader: true},
        {
            id: 2,
            title: "Unlimited Cashback on unlimited orders",
            description: "",
            tier: "professional",
            hasUnderline: true
        },
        {id: 3, title: "Unlimited customer accounts", description: "", tier: "professional"},
        {
            id: 4,
            title: "Shopify New Customer Accounts",
            description: "Shopify New Customer Accounts is the newest version of customer accounts, and lets your customers log in using a one-time 6-digit verification code sent by email.",
            tier: "professional"
        },
        {
            id: 5,
            title: "Essential for Professionals",
            description: "",
            tier: "professional",
            isHeader: true,
            marginTop: 'mt-8'
        },
        {
            id: 6,
            title: "Reviews, referrals, and loyalty program",
            description: "Rewards customers for reviews, referrals, and common loyalty activities.",
            tier: "professional"
        },
        {
            id: 7,
            title: "Refunds with store credit",
            description: "Refund any order in full or in part with store credit. Also supports refunding more than the original amount for a better refund experience.",
            tier: "professional"
        },
        {
            id: 8,
            title: "Signup Credit",
            description: "Rewards customers from day one. Issues instant bonuses for signing up.",
            tier: "professional"
        },
        {
            id: 9,
            title: "Direct integrations for Shopify",
            description: "Pabloo integrates seamlessly with the leading e-commerce platforms.",
            tier: "professional"
        },

        // --- Premium Tier Features (mapped from Desktop's Premium) ---
        {id: 10, title: "Everything in the Professional", description: "", tier: "premium", hasUnderline: true},
        {id: 11, title: "Memberships technology", description: "", tier: "premium", isHeader: true},
        {id: 12, title: "Unlimited Membership subscribers", description: "", tier: "premium"},
        {
            id: 13,
            title: "Attachable perks",
            description: "Customize every payout, perk, and tier to perfectly match brand identity.",
            tier: "premium"
        },
        {
            id: 14,
            title: "Pay-to-play or free entry",
            description: "Membership tiers that customers can buy through your Shopify store.",
            tier: "premium"
        },
        {
            id: 15,
            title: "Recurring Credit",
            description: "Schedule custom frequencies to keep engagement high.",
            tier: "premium"
        },
        {id: 16, title: "Dashboards and granular data tracking", description: "", tier: "premium"},
        {id: 17, title: "Premium Essentials", description: "", tier: "premium", isHeader: true},
        {
            id: 18,
            title: "Premium customer support assistant",
            description: "A dedicated customer support assistant will work closely with you to accomplish your goals.",
            tier: "premium"
        },
        {
            id: 19,
            title: "White labeled and customizable",
            description: "Customizable views to perfectly match your shop's theme and branding, ensuring a flawless experience across all devices.",
            tier: "premium"
        },
        {
            id: 20,
            title: "Opt-in affiliate program",
            description: "Each participating customer has an affiliate link; a portion of each sale generated through the link goes back to the affiliate.",
            tier: "premium"
        },
        {
            id: 21,
            title: "Email notifications",
            description: "Customers are kept informed every time their store credit is added, deducted, or earn cashback.",
            tier: "premium"
        },
    ];


    // *** REMOVED handleCheckout function ***

    // Function to render features, slightly adapted for mobile list
    const renderFeatures = (tier) => {
        return pricingFeatures
            .filter((value) => value.tier === tier)
            .map((value) => (
                <li
                    key={value.id}
                    className={`${
                        value.isHeader
                            ? "font-bold text-lg mt-4 mb-2 " + (value.marginTop || "")
                            : ""
                    } ${
                        value.hasUnderline
                            ? "pb-1 border-b border-dashed border-gray-400"
                            : ""
                    }`}
                >
                    <div className="font-semibold">{value.title}</div>
                </li>
            ));
    };

    return (
        <div
            className="flex justify-center"
            style={{marginTop: "-90px", marginBottom: "-150px"}} // Keep original inline styles
        >
            <section className="pricing">
                <div className="container solid-background">
                    <div className="pricing-plan__cta-subtitle mb-2 text-sm p-1 text-center">
                        Over 15% savings with annual billing
                    </div>
                    <div className="pricing-tabs">
                        <button
                            className={`pricing-tab ${
                                activeTab === "annually" ? "active" : ""
                            }`}
                            onClick={() => {
                                handleFrequencyChange("annually")
                            }}
                        >
                            Annually
                        </button>
                        <button
                            className={`pricing-tab ${
                                activeTab === "monthly" ? "active" : ""
                            }`}
                            onClick={() => {
                                handleFrequencyChange("monthly")
                            }}
                        >
                            Monthly
                        </button>
                    </div>


                    <TransitionGroup>
                        {activeTab === "annually" && (
                            <CSSTransition
                                nodeRef={annuallyRef}
                                key="annually"
                                timeout={300}
                                classNames="fade"
                            >
                                <div ref={annuallyRef} className="pricing-tab-content">
                                    {/* --- Pane 1: Professional (Annually) --- */}
                                    <div className="">
                                        <div className="pricing-pane">
                                            <div className="pricing-pane__header">
                                                <div className="pricing-pane__title">Professional</div>
                                                <div className="pricing-plan__tags justify-center">
                                                    <span className="pill">Solo entrepreneurs</span>
                                                    <span className="pill">Small teams</span>
                                                    <span className="pill">Scaling</span>
                                                </div>
                                            </div>
                                            <div className="pricing-pane__info">
                                                <div className="pricing-pane__info-price">
                                                    <span className="pricing-plan__price">$120</span>
                                                </div>
                                                <p>Monthly, billed yearly</p>
                                                {/* *** UPDATED BUTTON *** */}
                                                <a
                                                    href="/cart" // Navigate to cart
                                                    target="_self" // Stay in the same tab
                                                    className="button button--primary pricing-plan__button"
                                                    style={{
                                                        width: "80%",
                                                        marginBottom: "20px",
                                                        fontWeight: "700",
                                                    }}
                                                    onClick={() => {
                                                        const cartObject = {
                                                            productIndex: 1, // Professional index from desktop
                                                            frequency: "year", // Annually tab
                                                            quantity: 1,
                                                        };
                                                        addCartItem(cartObject); // Call helper
                                                    }}
                                                >
                                                    Continue with Professional
                                                </a>
                                            </div>
                                            <div className="pricing-pane__list">
                                                <ul className="" style={{margin: "0px", paddingLeft: "0px"}}>
                                                    {renderFeatures("professional")}
                                                </ul>
                                            </div>
                                        </div>
                                    </div>

                                    {/* --- Pane 2: Premium (Annually) --- */}
                                    <div className="pricing-tab__pane">
                                        <div className="pricing-pane popular">
                                            <div className="pricing-pane__label">MOST POPULAR</div>
                                            <div className="pricing-pane__header">
                                                <div className="pricing-pane__title">Premium</div>
                                                <div className="pricing-plan__tags justify-center">
                          <span className="pill" style={{marginLeft: "15px"}}>
                            Medium-sized teams
                          </span>
                                                    <span className="pill">Scaling</span>
                                                </div>
                                            </div>
                                            <div className="pricing-pane__info">
                                                <div className="pricing-pane__info-price">
                                                    <span className="pricing-plan__price">$225</span>
                                                </div>
                                                <p>Monthly, billed yearly</p>
                                                {/* *** UPDATED BUTTON *** */}
                                                <a
                                                    href="/cart" // Navigate to cart
                                                    target="_self" // Stay in the same tab
                                                    className="button button--primary pricing-plan__button"
                                                    style={{
                                                        width: "80%",
                                                        marginBottom: "20px",
                                                        fontWeight: "700",
                                                    }}
                                                    onClick={() => {
                                                        const cartObject = {
                                                            productIndex: 2, // Premium index from desktop
                                                            frequency: "year", // Annually tab
                                                            quantity: 1,
                                                        };
                                                        addCartItem(cartObject); // Call helper
                                                    }}
                                                >
                                                    Continue with Premium
                                                </a>
                                            </div>
                                            <div className="pricing-pane__list">
                                                <ul className="" style={{margin: "0px", paddingLeft: "0px"}}>
                                                    {renderFeatures("premium")}
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </CSSTransition>
                        )}

                        {activeTab === "monthly" && (
                            <CSSTransition
                                nodeRef={monthlyRef}
                                key="monthly"
                                timeout={300}
                                classNames="fade"
                            >
                                <div ref={monthlyRef} className="pricing-tab-content">
                                    {/* --- Pane 1: Professional (Monthly) --- */}
                                    <div className="">
                                        <div className="pricing-pane">
                                            <div className="pricing-pane__header">
                                                <div className="pricing-pane__title">Professional</div>
                                                <div className="pricing-plan__tags justify-center">
                                                    <span className="pill">Solo entrepreneurs</span>
                                                    <span className="pill">Small teams</span>
                                                    <span className="pill">Scaling</span>
                                                </div>
                                            </div>
                                            <div className="pricing-pane__info">
                                                <div className="pricing-pane__info-price">
                                                    <span className="pricing-plan__price">$125</span>
                                                </div>
                                                <p>Monthly</p>
                                                <p className="pricing-plan__billing pricing-plan__billing--small -mt-2 mb-2">
                                                    <span>Or <strong>$120/month</strong>, billed yearly</span>
                                                </p>
                                                {/* *** UPDATED BUTTON *** */}
                                                <a
                                                    href="/cart" // Navigate to cart
                                                    target="_self" // Stay in the same tab
                                                    className="button button--primary pricing-plan__button"
                                                    style={{
                                                        width: "80%",
                                                        marginBottom: "20px",
                                                        fontWeight: "700",
                                                    }}
                                                    onClick={() => {
                                                        const cartObject = {
                                                            productIndex: 1, // Professional index from desktop
                                                            frequency: "month", // Monthly tab
                                                            quantity: 1,
                                                        };
                                                        addCartItem(cartObject); // Call helper
                                                    }}
                                                >
                                                    Continue with Professional
                                                </a>
                                            </div>
                                            <div className="pricing-pane__list">
                                                <ul className="" style={{margin: "0px", paddingLeft: "0px"}}>
                                                    {renderFeatures("professional")}
                                                </ul>
                                            </div>
                                        </div>
                                    </div>

                                    {/* --- Pane 2: Premium (Monthly) --- */}
                                    <div className="pricing-tab__pane">
                                        <div className="pricing-pane popular">
                                            <div className="pricing-pane__label">MOST POPULAR</div>
                                            <div className="pricing-pane__header">
                                                <div className="pricing-pane__title">Premium</div>
                                                <div className="pricing-plan__tags justify-center">
                          <span className="pill" style={{marginLeft: "15px"}}>
                            Medium-sized teams
                          </span>
                                                    <span className="pill">Scaling</span>
                                                </div>
                                            </div>
                                            <div className="pricing-pane__info">
                                                <div className="pricing-pane__info-price">
                                                    <span className="pricing-plan__price">$260</span>
                                                </div>
                                                <p>Monthly</p>
                                                <p className="pricing-plan__billing pricing-plan__billing--small -mt-2 mb-2">
                                                    <span>Or <strong>$225/month</strong>, billed yearly</span>
                                                </p>
                                                {/* *** UPDATED BUTTON *** */}
                                                <a
                                                    href="/cart" // Navigate to cart
                                                    target="_self" // Stay in the same tab
                                                    className="button button--primary pricing-plan__button"
                                                    style={{
                                                        width: "80%",
                                                        marginBottom: "20px",
                                                        fontWeight: "700",
                                                    }}
                                                    onClick={() => {
                                                        const cartObject = {
                                                            productIndex: 2, // Premium index from desktop
                                                            frequency: "month", // Monthly tab
                                                            quantity: 1,
                                                        };
                                                        addCartItem(cartObject); // Call helper
                                                    }}
                                                >
                                                    Continue with Premium
                                                </a>
                                            </div>
                                            <div className="pricing-pane__list">
                                                <ul className="" style={{margin: "0px", paddingLeft: "0px"}}>
                                                    {renderFeatures("premium")}
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </CSSTransition>
                        )}
                    </TransitionGroup>
                </div>
            </section>
        </div>
    );
};

export default MobilePricingComponent;
